import React, { useState } from 'react';
import {
  FileCode,
  Truck,
  CheckCircle2,
  Clock,
  Download,
  QrCode,
  Sparkles
} from 'lucide-react';
import { useGst } from '../../context/GstContext';
import { MerchantInvoice } from '../../types/gst';
import {
  generateEInvoiceForInvoice,
  generateEWayBillForInvoice
} from '../../services/billingService';
import { EwbConsignmentModal } from '../../components/billing/EwbConsignmentModal';
import { toast } from '../../components/ui';

export const ComplianceHubPage: React.FC = () => {
  const { merchantInvoices, updateMerchantInvoice } = useGst();

  const [activeTab, setActiveTab] = useState<'EINVOICE' | 'EWAYBILL'>('EINVOICE');
  const [selectedInvoice, setSelectedInvoice] = useState<MerchantInvoice | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [ewbModalOpen, setEwbModalOpen] = useState<boolean>(false);

  // E-Invoicing applies to B2B and Export invoices
  const eligibleEInvoices = merchantInvoices.filter(
    inv => inv.invoiceType === 'B2B' || inv.invoiceType === 'EXPORT'
  );

  // E-Way Bill applies to invoices with value > 50,000
  const eligibleEwbInvoices = merchantInvoices.filter(inv => inv.grandTotal >= 50000);

  const handleGenerateEInvoice = async (inv: MerchantInvoice) => {
    setIsProcessing(true);
    try {
      const eInvoiceDetails = await generateEInvoiceForInvoice(inv);
      const updated: MerchantInvoice = {
        ...inv,
        eInvoice: eInvoiceDetails,
        updatedAt: new Date().toISOString()
      };
      await updateMerchantInvoice(updated);
      toast.success(`E-Invoice IRN successfully generated for #${inv.invoiceNumber}!`);
    } catch (err: any) {
      toast.error('Failed to generate E-Invoice: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenEwbModal = (inv: MerchantInvoice) => {
    setSelectedInvoice(inv);
    setEwbModalOpen(true);
  };

  const handleConfirmEwb = async (vehicleNo: string, transporterId: string, distanceKm: number) => {
    if (!selectedInvoice) return;
    setIsProcessing(true);
    try {
      const ewbDetails = generateEWayBillForInvoice(selectedInvoice, {
        vehicleNumber: vehicleNo,
        transporterId,
        distanceKm
      });
      const updated: MerchantInvoice = {
        ...selectedInvoice,
        eWayBill: ewbDetails,
        updatedAt: new Date().toISOString()
      };
      await updateMerchantInvoice(updated);
      toast.success(`E-Way Bill #${ewbDetails.ewbNumber} generated for #${selectedInvoice.invoiceNumber}!`);
      setEwbModalOpen(false);
    } catch (err: any) {
      toast.error('Failed to generate E-Way Bill: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportNicJson = (inv: MerchantInvoice) => {
    const payload = {
      Version: '1.1',
      TranDtls: {
        TaxSch: 'GST',
        SupTyp: inv.invoiceType,
        RegRev: 'N',
        EcmGstin: null,
        IgstOnIntra: 'N'
      },
      DocDtls: {
        Typ: 'INV',
        No: inv.invoiceNumber,
        Dt: inv.invoiceDate
      },
      SellerDtls: {
        Gstin: inv.sellerGstin,
        LglNm: inv.sellerLegalName,
        TrdNm: inv.sellerTradeName,
        Addr1: inv.sellerAddress,
        Loc: 'Mumbai',
        Pin: 400051,
        Stcd: inv.sellerStateCode
      },
      BuyerDtls: {
        Gstin: inv.customerGstin || 'URP',
        LglNm: inv.customerName,
        Pos: inv.placeOfSupply.substring(0, 2),
        Addr1: inv.customerBillingAddress || 'Address on record',
        Loc: 'Navi Mumbai',
        Pin: 400703,
        Stcd: inv.placeOfSupply.substring(0, 2)
      },
      ItemList: inv.lineItems.map((item, idx) => ({
        SlNo: String(idx + 1),
        PrdDesc: item.description,
        IsServc: item.hsnSac.startsWith('99') ? 'Y' : 'N',
        HsnCd: item.hsnSac,
        Qty: item.quantity,
        UnitPrice: item.unitPrice,
        TotAmt: item.taxableAmount,
        Discount: item.discountAmount,
        AssAmt: item.taxableAmount,
        GstRt: item.gstRate,
        IgstAmt: item.igstAmount,
        CgstAmt: item.cgstAmount,
        SgstAmt: item.sgstAmount,
        CesAmt: item.cessAmount,
        TotItemVal: item.totalAmount
      })),
      ValDtls: {
        AssVal: inv.subtotalTaxable,
        CgstVal: inv.totalCgst,
        SgstVal: inv.totalSgst,
        IgstVal: inv.totalIgst,
        CesVal: inv.totalCess,
        RndOffAmt: inv.roundOff,
        TotInvVal: inv.grandTotal
      }
    };

    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(payload, null, 2));
    const a = document.createElement('a');
    a.href = dataStr;
    a.download = `NIC_EINVOICE_${inv.invoiceNumber.replace(/[^A-Z0-9]/g, '_')}.json`;
    a.click();
    toast.success(`NIC E-Invoice payload exported for #${inv.invoiceNumber}`);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Title */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <FileCode className="h-6 w-6 text-cyan-400" />
            <span>E-Invoice (IRP) & E-Way Bill (Rule 138) Hub</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            NIC standard 64-character SHA-256 IRN, signed QR code payloads & vehicle consignment generation
          </p>
        </div>

        <div className="flex rounded-xl border border-white/10 bg-[#131316] p-1 text-xs">
          <button
            onClick={() => setActiveTab('EINVOICE')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-semibold transition ${
              activeTab === 'EINVOICE'
                ? 'bg-cyan-500 text-black shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <QrCode className="h-3.5 w-3.5" />
            <span>E-Invoices ({eligibleEInvoices.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('EWAYBILL')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-semibold transition ${
              activeTab === 'EWAYBILL'
                ? 'bg-cyan-500 text-black shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Truck className="h-3.5 w-3.5" />
            <span>E-Way Bills ({eligibleEwbInvoices.length})</span>
          </button>
        </div>
      </div>

      {/* Main Table */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131316]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0e1015] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Invoice # & Date</th>
              <th className="py-3 px-4">Customer & GSTIN</th>
              <th className="py-3 px-4 text-right">Invoice Value (₹)</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4">Reference & Identification</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300">
            {activeTab === 'EINVOICE' ? (
              eligibleEInvoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 font-sans">
                    No eligible B2B invoices for E-Invoicing.
                  </td>
                </tr>
              ) : (
                eligibleEInvoices.map(inv => {
                  const hasIrn = inv.eInvoice?.status === 'GENERATED';
                  return (
                    <tr key={inv.id} className="hover:bg-white/[0.02]">
                      <td className="py-3 px-4 font-bold text-white">
                        {inv.invoiceNumber}
                        <div className="text-[10px] text-slate-500 font-normal">{inv.invoiceDate}</div>
                      </td>

                      <td className="py-3 px-4 font-sans">
                        <div className="font-semibold text-white">{inv.customerName}</div>
                        <div className="font-mono text-[10px] text-slate-400">{inv.customerGstin || 'B2B Recipient'}</div>
                      </td>

                      <td className="py-3 px-4 text-right font-bold text-white">
                        ₹{inv.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-3 px-4 text-center font-sans">
                        {hasIrn ? (
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-bold">
                            <CheckCircle2 className="h-3 w-3" />
                            <span>IRN Generated</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 text-[10px] font-bold">
                            <Clock className="h-3 w-3" />
                            <span>Pending IRN</span>
                          </span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {hasIrn ? (
                          <div className="space-y-0.5">
                            <div className="text-[10px] text-cyan-300 truncate max-w-xs font-mono" title={inv.eInvoice?.irn}>
                              IRN: {inv.eInvoice?.irn}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              Ack No: {inv.eInvoice?.ackNo} &bull; {inv.eInvoice?.ackDate}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-[11px] font-sans">Click Generate to compute SHA-256 IRN hash</span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right font-sans">
                        <div className="flex items-center justify-end gap-1.5">
                          {hasIrn ? (
                            <button
                              onClick={() => handleExportNicJson(inv)}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-medium transition"
                              title="Download NIC JSON"
                            >
                              <Download className="h-3.5 w-3.5 text-cyan-400" />
                              <span>NIC JSON</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleGenerateEInvoice(inv)}
                              disabled={isProcessing}
                              className="flex items-center gap-1 px-3 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold transition shadow-sm"
                            >
                              <Sparkles className="h-3.5 w-3.5" />
                              <span>Generate IRN</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )
            ) : (
              eligibleEwbInvoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 font-sans">
                    No outward invoices above ₹50,000 threshold for E-Way Bill.
                  </td>
                </tr>
              ) : (
                eligibleEwbInvoices.map(inv => {
                  const hasEwb = inv.eWayBill?.status === 'GENERATED';
                  return (
                    <tr key={inv.id} className="hover:bg-white/[0.02]">
                      <td className="py-3 px-4 font-bold text-white">
                        {inv.invoiceNumber}
                        <div className="text-[10px] text-slate-500 font-normal">{inv.invoiceDate}</div>
                      </td>

                      <td className="py-3 px-4 font-sans">
                        <div className="font-semibold text-white">{inv.customerName}</div>
                        <div className="font-mono text-[10px] text-slate-400">{inv.customerBillingAddress || inv.placeOfSupply}</div>
                      </td>

                      <td className="py-3 px-4 text-right font-bold text-white">
                        ₹{inv.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-3 px-4 text-center font-sans">
                        {hasEwb ? (
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-bold">
                            <CheckCircle2 className="h-3 w-3" />
                            <span>EWB Active</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 text-[10px] font-bold">
                            <Clock className="h-3 w-3" />
                            <span>Pending EWB</span>
                          </span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {hasEwb ? (
                          <div className="space-y-0.5 text-[10px]">
                            <div className="text-emerald-300 font-mono font-bold">
                              EWB: {inv.eWayBill?.ewbNumber}
                            </div>
                            <div className="text-slate-400">
                              Vehicle: {inv.eWayBill?.vehicleNumber} &bull; Valid: {inv.eWayBill?.validUpto}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-[11px] font-sans">Rule 138: Mandatory for goods &gt; ₹50,000</span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right font-sans">
                        {hasEwb ? (
                          <button
                            onClick={() => handleOpenEwbModal(inv)}
                            className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-medium transition"
                          >
                            Update Vehicle
                          </button>
                        ) : (
                          <button
                            onClick={() => handleOpenEwbModal(inv)}
                            className="flex items-center gap-1 px-3 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold transition shadow-sm ml-auto"
                          >
                            <Truck className="h-3.5 w-3.5" />
                            <span>Generate EWB</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Generate / Update E-Way Bill Modal */}
      <EwbConsignmentModal
        isOpen={ewbModalOpen}
        onClose={() => setEwbModalOpen(false)}
        invoice={selectedInvoice}
        onConfirmEwb={handleConfirmEwb}
        isProcessing={isProcessing}
      />
    </div>
  );
};
